import { RoavaPage } from "@/components/roava/RoavaPage";
import { createFileRoute } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-shim";
import { useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import { format, formatDistanceToNow } from "date-fns";
import {
  CalendarIcon,
  Image as ImageIcon,
  Video as VideoIcon,
  MapPin,
  Search,
  Map as MapIcon,
  Grid3x3,
  Star,
  Ban,
  AlertTriangle,
  ExternalLink,
  Copy,
  SlidersHorizontal,
  Users,
  RefreshCw,
  X,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import type { DateRange } from "react-day-picker";
import { getMyTrip, getTripMembers } from "@/lib/trip.functions";
import { getTripLibrary, setMediaFlags, getTripTracks, reanalyzeMedia, getUploadRules, setMyMediaFamilyShared } from "@/lib/media.functions";
import { OfflineSaveButton } from "@/components/library/OfflineSaveButton";
import { useLocalMediaUrl } from "@/components/library/useLocalMediaUrl";

import { MediaTimeline } from "@/components/library/MediaTimeline";
import { CachedThumb } from "@/components/library/CachedThumb";
import { LibraryCacheScope } from "@/components/library/LibraryCacheScope";
import { PingsList } from "@/components/library/PingsList";
import {
  readLibrarySnapshot,
  writeLibrarySnapshotIdle,
} from "@/lib/cache/libraryCache";
import { pruneExpiredThumbs } from "@/lib/cache/thumbnailCache";

// Lazy: pulls in mapbox-gl (~700KB gz). Library defaults to grid view so cellular
// users never download the map chunk unless they tap "Map".
const MediaMap = lazy(() => import("@/components/MediaMap").then((m) => ({ default: m.MediaMap })));
import { MapErrorBoundary } from "@/components/MapErrorBoundary";
import { MapDebugPanel } from "@/components/library/MapDebugPanel";
import { useAuth } from "@/lib/auth";
import { TravelerVisibilityControl } from "@/components/library/TravelerVisibilityControl";
import {
  loadTravelerFilter,
  saveTravelerFilter,
  resolveVisibleIds,
  type TravelerFilter,
} from "@/lib/media-traveler-filter";
import { colorForUser } from "@/lib/traveler-colors";
import { Link } from "@tanstack/react-router";
import { FixStuckUploadsSheet } from "@/components/FixStuckUploadsSheet";
import { fixStuckUploads } from "@/lib/upload/fix-stuck";
import { useRetryUpload } from "@/lib/upload/use-retry-upload";
import { isRetryable } from "@/lib/upload/retry";
import { TripSwitcher } from "@/components/TripSwitcher";
import { useActiveTripId } from "@/lib/active-trip";
import { PendingPingsChip } from "@/components/PendingPingsChip";
import { kickGpsSyncAndDiag } from "@/lib/native/kick-gps-sync";
import { useTranslation } from "react-i18next";

type LibrarySearch = { user?: string; tag?: string };

export const Route = createFileRoute("/_authenticated/library")({
  head: () => pageMeta({ title: 'Trip Library — Roava by WGP Apps', description: 'Browse every photo and video your group has captured on the trip.', path: '/library' }),
  validateSearch: (search: Record<string, unknown>): LibrarySearch => {
    const out: LibrarySearch = {};
    if (typeof search.user === "string") out.user = search.user;
    if (typeof search.tag === "string") out.tag = search.tag;
    return out;
  },
  component: LibraryPage,
});

type SortKey = "captured_desc" | "captured_asc" | "uploaded_desc" | "uploaded_asc";

function formatBytes(n?: number | null) {
  if (!n) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${u[i]}`;
}

function formatDuration(s?: number | null) {
  if (!s) return "—";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
}

function metadataCompleteness(item: any): { score: 0 | 1 | 2 | 3; color: string; label: string } {
  let s = 0;
  if (item.captured_at) s++;
  if (item.latitude != null && item.longitude != null) s++;
  if (item.exif_json && Object.keys(item.exif_json).length > 0) s++;
  if (s === 3) return { score: 3, color: "bg-emerald-500", label: "Complete" };
  if (s === 2) return { score: 2, color: "bg-amber-400", label: "Mostly complete" };
  if (s === 1) return { score: 1, color: "bg-orange-500", label: "Partial" };
  return { score: 0, color: "bg-red-500", label: "Missing metadata" };
}

// Strip placeholder seed tag inserted by demo data — it's not a real AI result.
function realTags(tags: string[] | null | undefined): string[] {
  return (tags ?? []).filter((t) => t && t.toLowerCase() !== "seed");
}

function LibraryPage() {
  const { t } = useTranslation();
  const tripFn = useServerFn(getMyTrip);
  const libFn = useServerFn(getTripLibrary);
  const membersFn = useServerFn(getTripMembers);
  const trackFn = useServerFn(getTripTracks);
  const urlSearch = Route.useSearch();
  const { user } = useAuth();
  const currentUserId = user?.id ?? null;
  const activeTripId = useActiveTripId(currentUserId);
  // Key the query by the active trip selection so switching trips never reuses
  // a stale cached payload from a different trip.
  const { data: tripData } = useQuery({
    queryKey: ["my-trip", activeTripId ?? "default"],
    queryFn: () => tripFn({ data: { tripId: activeTripId ?? null } }),
  });
  const tripId = tripData?.trip?.id;

  const { data: membersData } = useQuery({
    queryKey: ["trip-members", tripId],
    queryFn: () => membersFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
    staleTime: 60_000,
  });
  const userColors = useMemo(() => {
    const map: Record<string, string | null> = {};
    for (const m of membersData?.members ?? []) map[m.userId] = m.color ?? null;
    return map;
  }, [membersData]);
  const userNames = useMemo(() => {
    const map: Record<string, string | null> = {};
    for (const m of membersData?.members ?? []) map[m.userId] = m.displayName ?? null;
    return map;
  }, [membersData]);

  // Pause background polling when tab is hidden — saves cell data + battery.
  const [visible, setVisible] = useState(typeof document === "undefined" || !document.hidden);
  useEffect(() => {
    const onVis = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const qc = useQueryClient();

  // Hydrate React Query with a cached snapshot (if any) so the grid paints
  // immediately on return visits. The live useQuery below still refetches
  // in the background and overwrites the snapshot.
  const hydratedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!tripId || !currentUserId) return;
    const k = `${currentUserId}:${tripId}`;
    if (hydratedKeyRef.current === k) return;
    hydratedKeyRef.current = k;
    (async () => {
      const snap = await readLibrarySnapshot(currentUserId, tripId, "all");
      if (!snap) return;
      // Only hydrate if React Query has no data yet; never clobber fresh data.
      const existing = qc.getQueryData(["trip-library", tripId]);
      if (existing) return;
      qc.setQueryData(["trip-library", tripId], snap);
    })();
  }, [tripId, currentUserId, qc]);

  // Idle-prune expired thumbnail records once per Library mount.
  useEffect(() => {
    pruneExpiredThumbs();
  }, []);

  // On Library mount (and again on app foreground), force-flush the native
  // LocationBG queue + JS outbox so any pings sitting on the phone reach the
  // server BEFORE the map paints. Also refreshes device_diagnostics so admins
  // can see the phone has checked in today. Without this, the map can show
  // an apparent Mongolia → Japan teleport while the in-between flight pings
  // are still buffered on-device. No-op on web / pre-plugin builds.
  useEffect(() => {
    if (!tripId) return;
    let cancelled = false;
    const kick = async () => {
      const res = await kickGpsSyncAndDiag(tripId);
      if (cancelled) return;
      // If anything actually moved, invalidate the trip-tracks query so the
      // map repaints with the newly-uploaded pings.
      if (res.drainedNative > 0 || res.syncedPings > 0) {
        qc.invalidateQueries({ queryKey: ["trip-tracks", tripId] });
      }
    };
    void kick();
    const onFocus = () => { if (document.visibilityState === "visible") void kick(); };
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [tripId, qc]);


  const { data, isLoading } = useQuery({
    queryKey: ["trip-library", tripId],
    queryFn: () => libFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
    staleTime: 30_000,
    // Only refetch on focus when uploads are in-flight; otherwise the 60s
    // poll-when-pending below is sufficient and cellular-friendly.
    refetchOnWindowFocus: (q) => {
      const items = ((q.state.data as any)?.items ?? []) as any[];
      return items.some((it) => it?.upload_status === "uploading" || it?.upload_status === "queued");
    },
    refetchInterval: (q) => {
      if (!visible) return false;
      const items = ((q.state.data as any)?.items ?? []) as any[];
      const pending = items.some((it) => it?.upload_status === "uploading" || it?.upload_status === "queued");
      return pending ? 5_000 : false;
    },
  });

  // After a successful network refresh, persist the trimmed snapshot.
  useEffect(() => {
    if (!data || !tripId || !currentUserId) return;
    writeLibrarySnapshotIdle(currentUserId, tripId, data, "all");
  }, [data, tripId, currentUserId]);

  // Trip GPS tracks overlay on the Media map (all travelers). Failure-safe.
  // Polls every 60s when visible (was 30s); pauses entirely when hidden.
  const { data: trackData, status: trackStatus, error: trackError } = useQuery({
    queryKey: ["trip-tracks", tripId, currentUserId],
    queryFn: () => trackFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
    staleTime: 30_000,
    refetchInterval: visible ? 60_000 : false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 1,
  });
  const allTracksByUser = (trackData?.tracksByUser ?? {}) as Record<
    string,
    { lat: number; lng: number; capturedAt?: string }[]
  >;
  useEffect(() => {
    if (!trackData) return;
    const counts: Record<string, number> = {};
    let total = 0;
    for (const [uid, pts] of Object.entries(allTracksByUser)) {
      counts[uid] = pts.length;
      total += pts.length;
    }
    const serverTotal = (trackData as any)?.serverTotal ?? null;
    // eslint-disable-next-line no-console
    console.log("[media-tracks] fetched trip tracks", {
      tripId,
      total,
      serverTotal,
      delta: serverTotal != null ? serverTotal - total : null,
      countsByUser: counts,
    });
    if (serverTotal != null && total < serverTotal) {
      // eslint-disable-next-line no-console
      console.warn("[media-tracks] UNDERCOUNT — map will display fewer pings than DB has", {
        total,
        serverTotal,
      });
    }
  }, [trackData]); // eslint-disable-line react-hooks/exhaustive-deps


  const items = (data?.items ?? []) as any[];
  const isAdmin = !!data?.isAdmin;

  // Upload rules — reused to honour the Wi-Fi-only preference in the offline pack flow.
  const uploadRulesFn = useServerFn(getUploadRules);
  const { data: uploadRules } = useQuery({
    queryKey: ["upload-rules", tripId],
    queryFn: () => uploadRulesFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
    staleTime: 60_000,
  });
  const offlineWifiOnly = uploadRules?.wifiOnly ?? true;

  // Photos only (no videos) with a currently-valid signed URL — Track B brief.
  const offlinePhotos = useMemo(
    () =>
      items
        .filter((it) => it.media_type === "photo" && it.upload_status === "uploaded" && it.url)
        .map((it) => ({
          id: it.id,
          storage_path: it.storage_path,
          file_size_bytes: it.file_size_bytes,
          updated_at: it.updated_at,
          mime_type: it.mime_type,
          url: it.url,
        })),
    [items],
  );

  // Track B: resume any in-progress offline pack on mount + on app foreground.
  useEffect(() => {
    if (!currentUserId || !tripId || offlinePhotos.length === 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const { resumeIfEnabled } = await import("@/lib/offline/tripPack");
        if (cancelled) return;
        await resumeIfEnabled(currentUserId, tripId, offlinePhotos, {
          wifiOnly: offlineWifiOnly,
        });
      } catch {
        /* ignore */
      }
    })();
    let detach: (() => void) | null = null;
    void (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const h = await App.addListener("appStateChange", async ({ isActive }) => {
          if (!isActive) return;
          const { resumeIfEnabled } = await import("@/lib/offline/tripPack");
          await resumeIfEnabled(currentUserId, tripId, offlinePhotos, {
            wifiOnly: offlineWifiOnly,
          });
        });
        detach = () => h.remove().catch(() => {});
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
      if (detach) detach();
    };
  }, [currentUserId, tripId, offlinePhotos, offlineWifiOnly]);

  // Filters (initialized from URL search params for cross-route deep links)
  const [search, setSearch] = useState(urlSearch.tag ?? "");
  // Media type multi-select. Empty set = "All".
  const [typeSet, setTypeSet] = useState<Set<"photo" | "video" | "ping">>(new Set());
  const toggleType = (tt: "photo" | "video" | "ping") => {
    setTypeSet((prev) => {
      const next = new Set(prev);
      if (next.has(tt)) next.delete(tt);
      else next.add(tt);
      return next;
    });
  };
  const isPingOnly = typeSet.size === 1 && typeSet.has("ping");
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [sort, setSort] = useState<SortKey>("captured_desc");
  const [view, setView] = useState<"grid" | "map">("grid");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mapStats, setMapStats] = useState<{
    mediaCount: number;
    pingCount: number;
    rawPingCount: number;
    travelerCount: number;
  } | null>(null);


  // Multi-traveler visibility filter (client-only, persisted per trip+user).
  // URL ?user= deep link wins on first load; otherwise restore from storage.
  const [travelerFilter, setTravelerFilter] = useState<TravelerFilter>(() =>
    urlSearch.user ? { mode: "selected", ids: [urlSearch.user] } : { mode: "all" },
  );
  const filterHydratedRef = useRef(false);
  useEffect(() => {
    if (filterHydratedRef.current) return;
    if (!tripId || !currentUserId) return;
    filterHydratedRef.current = true;
    if (urlSearch.user) return; // deep link beats persisted value
    const restored = loadTravelerFilter(tripId, currentUserId);
    setTravelerFilter(restored);
  }, [tripId, currentUserId, urlSearch.user]);
  // Persist on change (after hydration completes).
  useEffect(() => {
    if (!filterHydratedRef.current) return;
    saveTravelerFilter(tripId, currentUserId, travelerFilter);
    // eslint-disable-next-line no-console
    console.log(
      `[media-filter] mode=${travelerFilter.mode} selectedCount=${
        travelerFilter.mode === "selected" ? travelerFilter.ids.length : 0
      }`,
    );
  }, [tripId, currentUserId, travelerFilter]);

  // Warm thumbnails in the background once per (trip, scope) per session.
  // Pass user_id only when the user has narrowed to a single traveler.
  const warmScope = (() => {
    if (travelerFilter.mode === "me" && currentUserId) return currentUserId;
    if (
      travelerFilter.mode === "selected" &&
      travelerFilter.ids.length === 1
    )
      return travelerFilter.ids[0];
    return null;
  })();
  const warmedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!tripId) return;
    const key = `${tripId}:${warmScope ?? "all"}`;
    if (warmedRef.current.has(key)) return;
    warmedRef.current.add(key);
    (async () => {
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        await supabase.functions.invoke("warm-thumbnails", {
          body: {
            trip_id: tripId,
            ...(warmScope ? { user_id: warmScope } : {}),
          },
        });
      } catch (e) {
        console.warn("[warm-thumbnails] invoke failed", e);
      }
    })();
  }, [tripId, warmScope]);

  // Bridge events from drawer (uploader avatar / tag badge) back to filter state.
  // A single-traveler tap collapses the filter to that one traveler.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onUser = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      if (!id) return;
      setTravelerFilter({ mode: "selected", ids: [id] });
    };
    const onSearch = (e: Event) => setSearch((e as CustomEvent<string>).detail);
    window.addEventListener("library:set-user-filter", onUser);
    window.addEventListener("library:set-search", onSearch);
    return () => {
      window.removeEventListener("library:set-user-filter", onUser);
      window.removeEventListener("library:set-search", onSearch);
    };
  }, []);

  const uploaders = useMemo(() => {
    const m = new Map<string, { id: string; name: string; avatar: string | null }>();
    // Every trip member should be selectable, regardless of how many photos
    // they've uploaded (or whether their photos fit in the current page).
    for (const mem of membersData?.members ?? []) {
      m.set(mem.userId, { id: mem.userId, name: mem.displayName, avatar: mem.avatarUrl });
    }
    // Defensive: include any uploader not present in trip_members.
    for (const it of items) {
      if (!m.has(it.user_id)) {
        m.set(it.user_id, { id: it.user_id, name: it.uploader_name, avatar: it.uploader_avatar });
      }
    }
    return Array.from(m.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [items, membersData]);

  // Resolve the traveler filter into a concrete visibility set. `null` means
  // "show everyone the server returned". Member list comes from uploaders so
  // deleted users (e.g. seed cleanup) drop out automatically.
  const visibleIds = useMemo<Set<string> | null>(
    () => resolveVisibleIds(travelerFilter, uploaders.map((u) => u.id), currentUserId),
    [travelerFilter, uploaders, currentUserId],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = items.filter((it) => {
      if (visibleIds !== null && !visibleIds.has(it.user_id)) return false;
      if (typeSet.size > 0 && !typeSet.has(it.media_type as "photo" | "video" | "ping")) return false;
      const when = it.captured_at ? new Date(it.captured_at) : null;
      if (dateRange?.from && when && when < dateRange.from) return false;
      if (dateRange?.to) {
        const end = new Date(dateRange.to);
        end.setHours(23, 59, 59, 999);
        if (when && when > end) return false;
      }
      if (q) {
        const tags = realTags(it.ai_tags);
        if (!tags.some((t) => t.toLowerCase().includes(q))) return false;
      }
      return true;
    });
    arr = [...arr].sort((a, b) => {
      const field = sort.startsWith("captured") ? "captured_at" : "uploaded_at";
      const av = a[field] ? new Date(a[field]).getTime() : 0;
      const bv = b[field] ? new Date(b[field]).getTime() : 0;
      return sort.endsWith("asc") ? av - bv : bv - av;
    });
    return arr;
  }, [items, search, visibleIds, typeSet, dateRange, sort]);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(`[media-filter] visible media count: ${filtered.length}`);
  }, [filtered.length]);

  const selected = selectedId ? items.find((i) => i.id === selectedId) ?? null : null;

  // AI analysis progress + popular tags (computed across all loaded photos).
  const aiStats = useMemo(() => {
    let analyzed = 0;
    let pending = 0;
    const counts = new Map<string, number>();
    for (const it of items) {
      if (it.media_type !== "photo") continue;
      const status = (it.ai_status as string | null) ?? "pending";
      if (status === "complete") analyzed++;
      else pending++;
      for (const t of realTags(it.ai_tags)) {
        counts.set(t, (counts.get(t) ?? 0) + 1);
      }
    }
    const popular = Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([t]) => t);
    return { analyzed, pending, total: analyzed + pending, popular };
  }, [items]);

  // Build the per-user route line ONLY from real location_points. We used to
  // merge photo capture points in too, but two photos with GPS taken hours
  // apart in different places (e.g. a single airport selfie + a hotel shot)
  // create a phantom dashed segment labelled "likely flight" even though the
  // real route is a continuous solid line. Photos stay on the map as pins;
  // they no longer pollute the route polyline.
  //
  // Fallback: if a user has zero real route pings AND has photos with GPS,
  // we still draw a best-effort line through those photo points so the map
  // isn't empty for that user. The fallback kicks in per-user, not globally.
  const mergedTracksByUser = useMemo(() => {
    const out: Record<string, { lat: number; lng: number; capturedAt?: string }[]> = {};
    for (const [uid, pts] of Object.entries(allTracksByUser)) {
      if (pts.length > 0) out[uid] = pts.map((p) => ({ ...p }));
    }
    // Per-user fallback from photo GPS — ONLY when:
    //   (a) the track query has fully succeeded (not loading, not errored), AND
    //   (b) the backend explicitly reports rawCountByUser[uid] === 0.
    // Anything else (loading, error, missing entry) means "we don't know",
    // and we must NOT draw a fake photo route in that case.
    const serverRawCounts = ((trackData as any)?.rawCountByUser ?? {}) as Record<string, number>;
    const tracksOk = trackStatus === "success";
    const photoFallback: Record<string, { lat: number; lng: number; capturedAt?: string }[]> = {};
    if (tracksOk) {
      for (const it of filtered) {
        const lat = it.latitude;
        const lng = it.longitude;
        const uid = it.user_id;
        if (typeof lat !== "number" || typeof lng !== "number" || !uid) continue;
        if (out[uid]) continue; // has real route — never mix photo points in.
        if (!(uid in serverRawCounts)) continue; // backend didn't report this user.
        if ((serverRawCounts[uid] ?? 0) !== 0) continue; // real pings exist — never fake it.
        if (!photoFallback[uid]) photoFallback[uid] = [];
        photoFallback[uid].push({ lat, lng, capturedAt: it.captured_at ?? undefined });
      }
    }
    for (const [uid, pts] of Object.entries(photoFallback)) {
      out[uid] = pts;
    }
    for (const uid of Object.keys(out)) {
      out[uid].sort((a, b) => {
        const at = a.capturedAt ? new Date(a.capturedAt).getTime() : Number.POSITIVE_INFINITY;
        const bt = b.capturedAt ? new Date(b.capturedAt).getTime() : Number.POSITIVE_INFINITY;
        return at - bt;
      });
      // No client-side dedup. Stationary heartbeats from LocationBG are real
      // data (proving "you stopped here for N minutes"), not noise. The old
      // ~1m near-duplicate filter dropped ~31k of Chadd's 45.9k Mongolia
      // pings. Plot every ping the backend returned.
    }
    return out;
  }, [allTracksByUser, filtered, trackData, trackStatus]);

  // Per-user sorted timestamps (ms) of every ping the server returned for
  // each traveler, unaffected by the current view filter. MediaMap's gap
  // detector uses the GAP-OWNER's own series to decide whether to draw a
  // dashed "(likely flight)" arc — using all travelers combined would let
  // one person's home pings unsuppress someone else's transoceanic gap.
  const capturedAtMsByUser = useMemo<Record<string, number[]>>(() => {
    const out: Record<string, number[]> = {};
    for (const [uid, pts] of Object.entries(allTracksByUser)) {
      const ts: number[] = [];
      for (const p of pts) {
        if (!p.capturedAt) continue;
        const t = new Date(p.capturedAt).getTime();
        if (Number.isFinite(t)) ts.push(t);
      }
      ts.sort((a, b) => a - b);
      out[uid] = ts;
    }
    return out;
  }, [allTracksByUser]);

  // Raw (pre-dedup) per-user ping counts. Source of truth is the SERVER's
  // `rawCountByUser` head-counts — those reflect the real number of rows in
  // location_points for the trip, not the (capped + deduped) plotted line
  // length. This keeps the "N GPS pings" banner honest even when the per-user
  // 8k page cap is hit (e.g. Chadd's 45k+ historical points).
  const rawPingCountByUser = useMemo(() => {
    const out: Record<string, number> = {};
    const serverCounts = ((trackData as any)?.rawCountByUser ?? {}) as Record<string, number>;
    for (const [uid, n] of Object.entries(serverCounts)) {
      if (typeof n === "number" && n > 0) out[uid] = n;
    }
    // Backfill any user that has plotted route points but no server count
    // (shouldn't happen, but keeps the legend non-zero if the head query was
    // skipped for some reason).
    for (const [uid, pts] of Object.entries(allTracksByUser)) {
      if (out[uid] == null && pts.length > 0) out[uid] = pts.length;
    }
    return out;
  }, [trackData, allTracksByUser]);

  // Upload pipeline visibility — counts photos+videos this device thinks are
  // still owed to the server. Computed from `items` already in memory so this
  // costs nothing extra and is always in sync with the Library grid.
  const uploadQueueStats = useMemo(() => {
    let queued = 0, uploading = 0, failed = 0;
    for (const it of items) {
      if (it.user_id !== currentUserId) continue;
      if (it.upload_status === "queued") queued += 1;
      else if (it.upload_status === "uploading") uploading += 1;
      else if (it.upload_status === "failed") failed += 1;
    }
    return { queued, uploading, failed, pending: queued + uploading };
  }, [items, currentUserId]);

  const [fixStuckOpen, setFixStuckOpen] = useState(false);
  const retryMut = useRetryUpload(tripId ?? null);

  // Silent one-shot reconcile: when we see failed rows locally and we're
  // online, ask fixStuckUploads() to retry/finalize them. The chip then
  // disappears on its own once `my-media` is invalidated. Runs at most
  // once per tripId per mount to avoid hammering.
  const reconciledTripRef = useRef<string | null>(null);
  useEffect(() => {
    if (!tripId || reconciledTripRef.current === tripId) return;
    if (uploadQueueStats.failed <= 0) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    reconciledTripRef.current = tripId;
    (async () => {
      try {
        await fixStuckUploads(tripId);
        qc.invalidateQueries({ queryKey: ["my-media"] });
        qc.invalidateQueries({ queryKey: ["trip-library", tripId] });
      } catch {
        // silent — user can still tap the chip
      }
    })();
  }, [tripId, uploadQueueStats.failed, qc]);


  const activeFilterCount =
    (typeSet.size > 0 ? 1 : 0) +
    (dateRange?.from ? 1 : 0) +
    (sort !== "captured_desc" ? 1 : 0);

  const visibleTravelerCount = visibleIds === null ? uploaders.length : visibleIds.size;
  const travelerHelperText = (() => {
    if (travelerFilter.mode === "all") return null;
    if (travelerFilter.mode === "me") return t("library.showing_my");
    return t("library.showing_x_of_y", { visible: visibleTravelerCount, total: uploaders.length });
  })();

  const memoryCountLabel = (() => {
    if (isLoading) return t("library.loading");
    const total = items.length.toLocaleString();
    const filteredCount = filtered.length.toLocaleString();
    const truncated = (data as any)?.truncated ? t("library.truncated_suffix") : "";
    if (filtered.length === items.length) {
      return `${t(items.length === 1 ? "library.memory_one" : "library.memory_other", { n: total })}${truncated}`;
    }
    return `${t("library.filtered_of", { filtered: filteredCount, total })}${truncated}`;
  })();

  return (
    <LibraryCacheScope userId={currentUserId} tripId={tripId ?? null}>
    <RoavaPage
      title={t("library.title")}
      subtitle={memoryCountLabel}
      action={<TripSwitcher />}
    >




      {/* Filter bar — search + view toggle + Filters popover */}
      <div className="-mx-5 px-5 pb-3 bg-background/95 backdrop-blur border-b border-border space-y-2">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative w-full sm:flex-1 sm:min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("library.search_placeholder")}
              className="pl-9 h-9 w-full"
            />
          </div>
          <div className="flex gap-2 items-center">
          <div className="flex rounded-md border border-border overflow-hidden shrink-0">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setView("grid")}
              className={cn(
                "rounded-none h-9 px-2",
                view === "grid" && "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground",
              )}
              aria-label={t("library.grid_view_aria")}
            >
              <Grid3x3 className="size-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setView("map")}
              className={cn(
                "rounded-none h-9 px-2",
                view === "map" && "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground",
              )}
              aria-label={t("library.map_view_aria")}
            >
              <MapIcon className="size-4" />
            </Button>
          </div>
          <TravelerVisibilityControl
            travelers={uploaders}
            meId={currentUserId}
            filter={travelerFilter}
            onChange={setTravelerFilter}
            userColors={userColors}
          />
          {currentUserId && tripId && (
            <OfflineSaveButton
              userId={currentUserId}
              tripId={tripId}
              items={offlinePhotos}
              wifiOnly={offlineWifiOnly}
            />
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-9 gap-1.5 shrink-0",
                  activeFilterCount > 0 && "border-primary/60 text-primary",
                )}
              >
                <SlidersHorizontal className="size-4" />
                <span className="hidden sm:inline">{t("library.filters")}</span>
                {activeFilterCount > 0 && (
                  <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-4 space-y-4">
              <p className="text-[11px] text-muted-foreground -mt-1">
                {t("library.filters_hint")}
              </p>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t("library.media_type")}
                </label>
                <div className="grid grid-cols-2 gap-1.5">
                  {(["all", "photo", "video", "ping"] as const).map((tt) => {
                    const active =
                      tt === "all" ? typeSet.size === 0 : typeSet.has(tt);
                    return (
                      <button
                        key={tt}
                        onClick={() =>
                          tt === "all" ? setTypeSet(new Set()) : toggleType(tt)
                        }
                        className={cn(
                          "px-3 py-1.5 rounded-md text-sm transition-colors border",
                          active
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {tt === "all" ? t("library.type_all") : tt === "ping" ? t("library.type_pings") : tt === "photo" ? t("library.type_photos") : t("library.type_videos")}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t("library.date_range")}
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        "w-full justify-start gap-2 h-9",
                        dateRange?.from && "border-primary/60 text-primary",
                      )}
                    >
                      <CalendarIcon className="size-4" />
                      {dateRange?.from
                        ? dateRange.to
                          ? `${format(dateRange.from, "MMM d")} – ${format(dateRange.to, "MMM d")}`
                          : format(dateRange.from, "MMM d")
                        : t("library.any_date")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={setDateRange}
                      numberOfMonths={1}
                      className={cn("p-3 pointer-events-auto")}
                    />
                    {dateRange && (
                      <div className="p-2 border-t flex justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDateRange(undefined)}
                        >
                          {t("library.clear")}
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t("library.sort")}
                </label>
                <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
                  <SelectTrigger className="w-full h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="captured_desc">{t("library.sort_captured_newest")}</SelectItem>
                    <SelectItem value="captured_asc">{t("library.sort_captured_oldest")}</SelectItem>
                    <SelectItem value="uploaded_desc">{t("library.sort_uploaded_newest")}</SelectItem>
                    <SelectItem value="uploaded_asc">{t("library.sort_uploaded_oldest")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-muted-foreground"
                  onClick={() => {
                    setTravelerFilter({ mode: "all" });
                    setTypeSet(new Set());
                    setDateRange(undefined);
                    setSort("captured_desc");
                  }}
                >
                  {t("library.reset_all")}
                </Button>
              )}
            </PopoverContent>
          </Popover>
          </div>
        </div>

        {/* AI progress + popular tags (only when there are photos to analyze) */}
        {aiStats.total > 0 && (
          <div className="space-y-1.5">
            {aiStats.pending > 0 && (
              <p className="text-[11px] text-muted-foreground">
                {t("library.ai_progress", { done: aiStats.analyzed.toLocaleString(), total: aiStats.total.toLocaleString() })}
                {aiStats.pending > 0 && t("library.ai_pending_suffix", { n: aiStats.pending.toLocaleString() })}
              </p>
            )}
            {aiStats.popular.length > 0 && (
              <div className="flex items-center gap-1.5 overflow-x-auto -mx-1 px-1 pb-0.5">
                {aiStats.popular.map((tg) => {
                  const active = search.trim().toLowerCase() === tg.toLowerCase();
                  return (
                    <button
                      key={tg}
                      onClick={() => setSearch(active ? "" : tg)}
                      className={cn(
                        "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium shrink-0 transition-colors",
                        active
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-foreground/80 hover:bg-muted/80",
                      )}
                    >
                      {tg}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}


        {(uploadQueueStats.pending > 0 || uploadQueueStats.failed > 0) && (
          <div className="flex items-center gap-1.5 flex-wrap pb-0.5">
            {uploadQueueStats.pending > 0 && (
              <Link
                to="/my-uploads"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 text-xs font-medium hover:bg-amber-500/25 shrink-0"
              >
                {t(uploadQueueStats.pending === 1 ? "library.uploads_waiting_one" : "library.uploads_waiting_other", { n: uploadQueueStats.pending.toLocaleString() })}
                {offlineWifiOnly && t("library.wifi_only_suffix")}
              </Link>
            )}
            {uploadQueueStats.failed > 0 && (
              <button
                type="button"
                onClick={() => setFixStuckOpen(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-destructive/15 text-destructive text-xs font-medium hover:bg-destructive/25 shrink-0"
              >
                {t("library.failed_chip", { n: uploadQueueStats.failed.toLocaleString() })}
              </button>
            )}
            <Link
              to="/offline-control"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-muted text-foreground/80 text-xs font-medium hover:bg-muted/70 shrink-0"
            >
              {t("library.offline_settings")}
            </Link>
          </div>
        )}

        {(activeFilterCount > 0 || travelerHelperText) && (
          <div className="flex items-center gap-1.5 overflow-x-auto -mx-1 px-1 pb-0.5">
            {travelerHelperText && (
              <span className="text-[11px] text-muted-foreground shrink-0">
                {travelerHelperText}
              </span>
            )}
            {dateRange?.from && (
              <button
                onClick={() => setDateRange(undefined)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15 shrink-0"
              >
                {dateRange.to
                  ? `${format(dateRange.from, "MMM d")} – ${format(dateRange.to, "MMM d")}`
                  : format(dateRange.from, "MMM d")}
                <X className="size-3" />
              </button>
            )}
            {Array.from(typeSet).map((tt) => (
              <button
                key={tt}
                onClick={() => toggleType(tt)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15 shrink-0"
              >
                {tt === "ping" ? t("library.type_pings") : tt === "photo" ? t("library.type_photos") : t("library.type_videos")}
                <X className="size-3" />
              </button>
            ))}
            {sort !== "captured_desc" && (
              <button
                onClick={() => setSort("captured_desc")}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15 shrink-0"
              >
                {sort === "captured_asc"
                  ? t("library.sort_captured_oldest_chip")
                  : sort === "uploaded_desc"
                  ? t("library.sort_uploaded_newest_chip")
                  : t("library.sort_uploaded_oldest_chip")}
                <X className="size-3" />
              </button>
            )}
          </div>
        )}
      </div>


      {isPingOnly && view !== "map" ? (
        <PingsList
          tripId={tripId ?? null}
          visibleIds={visibleIds}
          dateRange={dateRange}
          sort={sort}
          userColors={userColors}
          userNames={userNames}
        />
      ) : view === "map" ? (
        <>
          {mapStats && (mapStats.mediaCount > 0 || mapStats.rawPingCount > 0) && (() => {
            const totalTravelers = uploaders.length;
            const hiddenCount = visibleIds === null ? 0 : Math.max(0, totalTravelers - mapStats.travelerCount);
            return (
              <div className="mb-2 space-y-0.5">
                <p className="text-[11px] leading-tight text-muted-foreground">
                  {hiddenCount > 0
                    ? t(totalTravelers === 1 ? "library.map_stats_hidden_one" : "library.map_stats_hidden_other", {
                        m: mapStats.mediaCount.toLocaleString(),
                        p: mapStats.rawPingCount.toLocaleString(),
                        count: mapStats.travelerCount.toLocaleString(),
                        total: totalTravelers.toLocaleString(),
                      })
                    : t(totalTravelers === 1 ? "library.map_stats_one" : "library.map_stats_other", {
                        m: mapStats.mediaCount.toLocaleString(),
                        p: mapStats.rawPingCount.toLocaleString(),
                        count: mapStats.travelerCount.toLocaleString(),
                      })}
                </p>
                {hiddenCount > 0 && (
                  <p className="text-[11px] leading-tight text-muted-foreground">
                    {t(hiddenCount === 1 ? "library.hidden_one" : "library.hidden_other", { n: hiddenCount.toLocaleString() })}{" "}
                    <button
                      type="button"
                      onClick={() => setTravelerFilter({ mode: "all" })}
                      className="underline underline-offset-2 hover:text-foreground"
                    >
                      {t("library.show_all")}
                    </button>
                  </p>
                )}
                {mapStats.rawPingCount !== mapStats.pingCount && (
                  <p className="text-[11px] leading-tight text-muted-foreground/80">
                    {t("library.merged_pings", { n: mapStats.pingCount.toLocaleString() })}
                  </p>
                )}
              </div>
            );
          })()}

          <div className="mb-2">
            <PendingPingsChip />
          </div>
          {(isAdmin ||
            (typeof window !== "undefined" &&
              window.localStorage?.getItem("roava:debug:map") === "1")) && (
            <MapDebugPanel
              rawCountByUser={rawPingCountByUser}
              fetchedCountByUser={(trackData as any)?.fetchedCountByUser ?? {}}
              outlierCountByUser={(trackData as any)?.outlierCountByUser ?? {}}
              simplifiedCountByUser={(trackData as any)?.simplifiedCountByUser ?? {}}
              allTracksByUser={allTracksByUser}
              plottedTracksByUser={mergedTracksByUser}
              truncated={!!(trackData as any)?.truncated}
              travelerFilter={travelerFilter}
              visibleIds={visibleIds}
              nameById={userNames as unknown as Record<string, string>}
              currentUserId={currentUserId}
              photoPinCount={filtered.reduce(
                (n, it) =>
                  typeof it.latitude === "number" && typeof it.longitude === "number"
                    ? n + 1
                    : n,
                0,
              )}
              dateRange={dateRange as { from?: Date; to?: Date } | undefined}
              trackStatus={trackStatus}
              trackError={trackError as { message?: string } | null}
              tripId={tripId ?? null}
              tripName={(tripData as any)?.trip?.name ?? null}
              serverTotal={(trackData as any)?.serverTotal ?? null}
            />

          )}
          <MapErrorBoundary>
            <Suspense
              fallback={
                <div className="w-full aspect-[4/3] md:aspect-[16/9] rounded-2xl bg-muted animate-pulse" />
              }
            >
              <MediaMap
                items={filtered}
                onSelect={setSelectedId}
                onStatsChange={setMapStats}
                userColors={userColors}
                userNames={userNames}
                tracksByUser={
                  visibleIds === null
                    ? mergedTracksByUser
                    : Object.fromEntries(
                        Object.entries(mergedTracksByUser).filter(([uid]) =>
                          visibleIds.has(uid),
                        ),
                      )
                }
                rawPingCountByUser={
                  visibleIds === null
                    ? rawPingCountByUser
                    : Object.fromEntries(
                        Object.entries(rawPingCountByUser).filter(([uid]) =>
                          visibleIds.has(uid),
                        ),
                      )
                }
                capturedAtMsByUser={capturedAtMsByUser}
              />
            </Suspense>
          </MapErrorBoundary>
          <p className="mt-2 text-xs text-muted-foreground">
            {t("library.map_caption_prefix")}
            <Link to="/tracking" className="underline underline-offset-2 text-primary">
              {t("library.route_tab")}
            </Link>
            {t("library.map_caption_suffix")}
          </p>
        </>

      ) : filtered.length === 0 ? (
        <div className="mt-12 text-center text-muted-foreground text-sm">
          {isLoading ? t("library.loading_library") : t("library.empty_filters")}
        </div>
      ) : (
        <MediaTimeline
          items={filtered}
          onSelect={(it) => setSelectedId(it.id)}
          onRetryFailed={(id) => retryMut.mutate(id)}
        />

      )}

      <DetailDrawer
        item={selected}
        isAdmin={isAdmin}
        tripId={tripId}
        userId={currentUserId}
        onClose={() => setSelectedId(null)}
      />
      <FixStuckUploadsSheet
        open={fixStuckOpen}
        onOpenChange={setFixStuckOpen}
        tripId={tripId ?? null}
        pending={uploadQueueStats.failed}
      />
    </RoavaPage>
    </LibraryCacheScope>
  );
}




function MediaCard({ item, onClick }: { item: any; onClick: () => void }) {
  const { t } = useTranslation();
  const meta = metadataCompleteness(item);
  const isVideo = item.media_type === "video";
  const when = item.captured_at ? new Date(item.captured_at) : null;

  // IntersectionObserver gate: don't resolve/download the thumbnail until the
  // card is near the viewport. Big grids used to eagerly fetch every tile on
  // mount — the top ~90% of Roava's Cloud spend is media egress.
  const rootRef = useRef<HTMLButtonElement | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting) { setInView(true); io.disconnect(); break; }
      },
      { rootMargin: "600px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  return (
    <button
      ref={rootRef}
      onClick={onClick}
      className="group text-left rounded-lg overflow-hidden bg-muted border border-border hover:border-primary transition-colors"
    >
      <div className="relative aspect-square bg-muted">
        {!isVideo && inView ? (
          <CachedThumb
            mediaId={item.id}
            w={200}
            storagePath={(item as { storage_path?: string | null }).storage_path ?? null}
            className="w-full h-full object-cover"
          />
        ) : isVideo ? (
          <div className="w-full h-full grid place-items-center text-muted-foreground">
            <VideoIcon className="size-8" />
          </div>
        ) : null}

        <div className="absolute top-1.5 left-1.5 size-6 rounded-full bg-black/55 grid place-items-center">
          {isVideo ? (
            <VideoIcon className="size-3.5 text-white" />
          ) : (
            <ImageIcon className="size-3.5 text-white" />
          )}
        </div>

        {item.upload_status !== "uploaded" && (
          <Badge
            variant="secondary"
            className="absolute top-1.5 right-1.5 text-[10px] capitalize px-1.5 py-0"
          >
            {item.upload_status}
          </Badge>
        )}

        <div className="absolute bottom-1.5 left-1.5 flex gap-1">
          {item.is_favorite && (
            <span className="size-5 rounded-full bg-amber-400 grid place-items-center">
              <Star className="size-3 text-black fill-black" />
            </span>
          )}
          {item.excluded_from_montage && (
            <span className="size-5 rounded-full bg-red-500 grid place-items-center">
              <Ban className="size-3 text-white" />
            </span>
          )}
          {item.needs_review && (
            <span className="size-5 rounded-full bg-orange-500 grid place-items-center">
              <AlertTriangle className="size-3 text-white" />
            </span>
          )}
        </div>

        <span
          title={meta.label}
          className={cn("absolute bottom-1.5 right-1.5 size-2.5 rounded-full ring-2 ring-black/40", meta.color)}
        />
      </div>

      <div className="p-2 space-y-0.5">
        <div className="text-xs font-medium truncate">{item.uploader_name}</div>
        <div className="text-[11px] text-muted-foreground flex items-center gap-1">
          {when ? formatDistanceToNow(when, { addSuffix: true }) : t("library.no_date")}
          {item.latitude != null && item.longitude != null && (
            <MapPin className="size-3 ml-auto" />
          )}
        </div>
      </div>
    </button>
  );
}

function DetailDrawer({
  item,
  isAdmin,
  tripId,
  userId,
  onClose,
}: {
  item: any | null;
  isAdmin: boolean;
  tripId: string | undefined;
  userId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const setFlagsFn = useServerFn(setMediaFlags);
  const flagsMut = useMutation({
    mutationFn: (vars: { mediaId: string; flag: "isFavorite" | "excludedFromMontage" | "needsReview"; value: boolean }) =>
      setFlagsFn({ data: { mediaId: vars.mediaId, [vars.flag]: vars.value } as any }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trip-library", tripId] }),
    onError: (e: any) => toast.error(e?.message ?? t("library.toast_update_failed")),
  });
  const reanalyzeFn = useServerFn(reanalyzeMedia);
  const reanalyzeMut = useMutation({
    mutationFn: (mediaId: string) => reanalyzeFn({ data: { mediaId } }),
    onSuccess: (res: any) => {
      if (res?.ok === false) toast.error(res.error ?? t("library.toast_ai_failed"));
      else toast.success(t("library.toast_ai_queued"));
      qc.invalidateQueries({ queryKey: ["trip-library", tripId] });
    },
    onError: (e: any) => toast.error(e?.message ?? t("library.toast_reanalysis_failed")),
  });
  const familyShareFn = useServerFn(setMyMediaFamilyShared);
  const familyShareMut = useMutation({
    mutationFn: (vars: { mediaId: string; value: boolean }) =>
      familyShareFn({ data: { mediaId: vars.mediaId, familyShared: vars.value } }),
    onMutate: (vars) => {
      // Optimistic toast.
      toast.success(vars.value ? t("library.toast_shared_family_on") : t("library.toast_shared_family_off"));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trip-library", tripId] }),
    onError: (e: any) => toast.error(e?.message ?? t("library.toast_update_failed")),
  });
  const isOwner = !!userId && item?.user_id === userId;

  const open = !!item;
  // Track B: local-first full-size lookup. Falls back to signed URL when missing.
  const localFullUrl = useLocalMediaUrl(userId, tripId ?? null, item?.id ?? null);
  const networkOnline = typeof navigator === "undefined" ? true : navigator.onLine;
  const photoSrc =
    item?.media_type === "photo" ? localFullUrl ?? item?.url ?? null : null;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+6rem)]">
        {item && (
          <>
            <SheetHeader className="pr-10">
              <SheetTitle className="truncate text-base">
                {item.captured_at
                  ? format(new Date(item.captured_at), "PP · p")
                  : item.uploaded_at
                  ? format(new Date(item.uploaded_at), "PP · p")
                  : item.media_type === "video"
                  ? t("library.untitled_video")
                  : t("library.untitled_photo")}
              </SheetTitle>
              {item.original_filename && (
                <div className="text-xs text-muted-foreground truncate">{item.original_filename}</div>
              )}
            </SheetHeader>

            <div className="mt-4 space-y-5">
              <div className="rounded-lg overflow-hidden bg-muted aspect-video grid place-items-center">
                {photoSrc && item.media_type === "photo" ? (
                  <img src={photoSrc} alt="" className="w-full h-full object-contain" />
                ) : !photoSrc && item.media_type === "photo" && !networkOnline ? (
                  <div className="text-sm text-muted-foreground px-4 text-center">
                    {t("library.offline_not_saved")}
                  </div>
                ) : item.url && item.media_type === "video" ? (
                  <video src={item.url} controls className="w-full h-full" />
                ) : item.upload_status === "uploading" || item.upload_status === "queued" ? (
                  <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground px-4 text-center">
                    <div className="size-6 border-2 border-muted-foreground/30 border-t-foreground rounded-full animate-spin" />
                    <span>{t("library.uploading_inline", { type: item.media_type === "video" ? t("library.media_video") : t("library.media_photo") })}</span>
                  </div>
                ) : item.upload_status === "failed" ? (
                  <FailedUploadInline mediaId={item.id} tripId={tripId ?? null} lastError={item.last_error} />
                ) : (
                  <div className="text-muted-foreground text-sm">{t("library.no_preview")}</div>
                )}
              </div>

              <button
                type="button"
                onClick={() => {
                  if (item.user_id) {
                    // Filter library to this uploader and close drawer
                    const ev = new CustomEvent("library:set-user-filter", { detail: item.user_id });
                    window.dispatchEvent(ev);
                  }
                  onClose();
                }}
                className="flex items-center gap-3 w-full text-left rounded-lg p-2 -mx-2 hover:bg-muted/60 active:bg-muted transition"
              >
                <Avatar className="size-9">
                  {item.uploader_avatar && <AvatarImage src={item.uploader_avatar} />}
                  <AvatarFallback>{(item.uploader_name ?? "?").slice(0, 1)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{item.uploader_name}</div>
                  <div className="text-xs text-muted-foreground">{t("library.tap_filter_uploader")}</div>
                </div>
              </button>

              {isOwner && item.media_type === "photo" && (
                <div className="flex items-start justify-between gap-3 rounded-lg border border-border/60 bg-muted/30 p-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-sm font-medium">
                      <Users className="size-4 text-primary" />
                      {t("library.share_family")}
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {t("library.share_family_desc_prefix")}<em>{t("library.share_family_desc_em")}</em>{t("library.share_family_desc_suffix")}
                    </p>
                  </div>
                  <Switch
                    checked={!!item.family_shared}
                    disabled={familyShareMut.isPending}
                    onCheckedChange={(v) => familyShareMut.mutate({ mediaId: item.id, value: v })}
                  />
                </div>
              )}


              <Section title={t("library.section_capture")}>
                <Row label={t("library.row_captured")} value={item.captured_at ? format(new Date(item.captured_at), "PPpp") : "—"} />
                {isAdmin && (
                  <Row
                    label={t("library.row_uploaded")}
                    value={item.uploaded_at ? format(new Date(item.uploaded_at), "PPpp") : "—"}
                  />
                )}
                <Row label={t("library.row_type")} value={item.media_type} />
                {item.media_type === "video" && (
                  <Row label={t("library.row_duration")} value={formatDuration(item.duration_seconds)} />
                )}
                {isAdmin && <Row label={t("library.row_size")} value={formatBytes(item.file_size_bytes)} />}
                {isAdmin && item.width && item.height && (
                  <Row label={t("library.row_dimensions")} value={`${item.width} × ${item.height}`} />
                )}
                {(item.camera_make || item.camera_model) && (
                  <Row label={t("library.row_camera")} value={`${item.camera_make ?? ""} ${item.camera_model ?? ""}`.trim()} />
                )}
              </Section>

              <Section title={t("library.section_location")}>
                {item.latitude != null && item.longitude != null ? (
                  <>
                    <Row label={t("library.row_gps")} value={`${item.latitude.toFixed(5)}, ${item.longitude.toFixed(5)}`} />
                    <Row label={t("library.row_altitude")} value={item.altitude_meters != null ? `${item.altitude_meters.toFixed(1)} m` : "—"} />
                    <Row label={t("library.row_heading")} value={item.heading_degrees != null ? `${item.heading_degrees.toFixed(0)}°` : "—"} />
                    <a
                      href={`https://www.google.com/maps?q=${item.latitude},${item.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      {t("library.open_in_maps")} <ExternalLink className="size-3" />
                    </a>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">{t("library.no_gps")}</p>
                )}
              </Section>

              {/* AI analysis: admin/debug only for now.
                  TODO: expose user-facing chips, filters, captions, and smart
                  albums once the analysis quality has been reviewed. */}
              {isAdmin && (
                <Section title={t("library.section_ai_admin")}>
                  <Row label={t("library.row_status")} value={item.ai_status ?? "pending"} />
                  {item.ai_error && <Row label={t("library.row_error")} value={item.ai_error} />}
                  {item.ai_processed_at && (
                    <Row label={t("library.row_processed")} value={format(new Date(item.ai_processed_at), "PPpp")} />
                  )}
                  {item.ai_caption && <Row label={t("library.row_caption")} value={item.ai_caption} />}
                  {item.ai_scene_summary && (
                    <Row label={t("library.row_scene")} value={item.ai_scene_summary} />
                  )}
                  {item.ai_category && <Row label={t("library.row_category")} value={item.ai_category} />}
                  {item.ai_activity && <Row label={t("library.row_activity")} value={item.ai_activity} />}
                  {item.ai_travel_moment && (
                    <Row label={t("library.row_moment")} value={item.ai_travel_moment} />
                  )}
                  {item.ai_quality && <Row label={t("library.row_quality")} value={item.ai_quality} />}
                  {item.ai_visual_priority != null && (
                    <Row label={t("library.row_priority")} value={String(item.ai_visual_priority)} />
                  )}
                  <Row label={t("library.row_highlight")} value={item.ai_is_highlight ? t("library.yes") : t("library.no")} />
                  {item.ai_contains_people != null && (
                    <Row label={t("library.row_people")} value={item.ai_contains_people ? t("library.yes") : t("library.no")} />
                  )}
                  {item.ai_contains_text != null && (
                    <Row label={t("library.row_text")} value={item.ai_contains_text ? t("library.yes") : t("library.no")} />
                  )}
                  {item.ai_detected_text && (
                    <Row label={t("library.row_detected_text")} value={item.ai_detected_text} />
                  )}
                  {(item.ai_location_clues ?? []).length > 0 && (
                    <Row label={t("library.row_location_clues")} value={(item.ai_location_clues as string[]).join(", ")} />
                  )}
                  {item.ai_story_value && (
                    <Row label={t("library.row_story_value")} value={item.ai_story_value} />
                  )}
                  {(item.ai_privacy_flags ?? []).length > 0 && (
                    <Row label={t("library.row_privacy_flags")} value={(item.ai_privacy_flags as string[]).join(", ")} />
                  )}
                  {realTags(item.ai_tags).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {realTags(item.ai_tags).map((tg) => (
                        <Badge key={tg} variant="secondary" className="text-xs">{tg}</Badge>
                      ))}
                    </div>
                  )}
                  {item.ai_quality_score != null && (
                    <Row label={t("library.row_legacy_quality")} value={item.ai_quality_score.toFixed(2)} />
                  )}
                  {item.media_type === "photo" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2"
                      disabled={reanalyzeMut.isPending}
                      onClick={() => reanalyzeMut.mutate(item.id)}
                    >
                      {reanalyzeMut.isPending ? t("library.reanalyzing") : t("library.reanalyze")}
                    </Button>
                  )}
                </Section>
              )}

              {isAdmin && (
                <Section title={t("library.section_storage")}>
                  <div className="flex items-start gap-2">
                    <code className="flex-1 text-[11px] bg-muted px-2 py-1 rounded break-all">
                      {item.storage_path ?? "—"}
                    </code>
                    {item.storage_path && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          navigator.clipboard.writeText(item.storage_path);
                          toast.success(t("library.copied"));
                        }}
                      >
                        <Copy className="size-3.5" />
                      </Button>
                    )}
                  </div>
                </Section>
              )}

              {isAdmin && (
                <Section title={t("library.section_exif")}>
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground">
                      {t("library.view_exif")}
                    </summary>
                    <pre className="mt-2 bg-muted p-2 rounded max-h-64 overflow-auto text-[11px]">
                      {JSON.stringify(item.exif_json ?? {}, null, 2)}
                    </pre>
                  </details>
                </Section>
              )}

              {isAdmin && (
                <Section title={t("library.section_admin")}>
                  <FlagSwitch
                    label={t("library.flag_favorite")}
                    icon={<Star className="size-4" />}
                    checked={!!item.is_favorite}
                    onChange={(v) =>
                      flagsMut.mutate({ mediaId: item.id, flag: "isFavorite", value: v })
                    }
                  />
                  <FlagSwitch
                    label={t("library.flag_exclude")}
                    icon={<Ban className="size-4" />}
                    checked={!!item.excluded_from_montage}
                    onChange={(v) =>
                      flagsMut.mutate({ mediaId: item.id, flag: "excludedFromMontage", value: v })
                    }
                  />
                  <FlagSwitch
                    label={t("library.flag_needs_review")}
                    icon={<AlertTriangle className="size-4" />}
                    checked={!!item.needs_review}
                    onChange={(v) =>
                      flagsMut.mutate({ mediaId: item.id, flag: "needsReview", value: v })
                    }
                  />
                </Section>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right break-all">{value}</span>
    </div>
  );
}

function FlagSwitch({
  label,
  icon,
  checked,
  onChange,
}: {
  label: string;
  icon: React.ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 py-1.5">
      <span className="flex items-center gap-2 text-sm">
        {icon}
        {label}
      </span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

function FailedUploadInline({
  mediaId,
  tripId,
  lastError,
}: {
  mediaId: string;
  tripId: string | null;
  lastError?: string | null;
}) {
  const { t } = useTranslation();
  const retryMut = useRetryUpload(tripId);
  const [retryable, setRetryable] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    isRetryable(mediaId).then((ok) => {
      if (!cancelled) setRetryable(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [mediaId]);
  return (
    <div className="flex flex-col items-center gap-2 px-4 text-center">
      <p className="text-sm text-destructive font-medium">{t("library.failed_inline_title")}</p>
      {lastError && (
        <p className="text-[11px] text-muted-foreground line-clamp-2 break-words">{lastError}</p>
      )}
      {retryable === false ? (
        <p className="text-xs text-muted-foreground">
          {t("library.failed_inline_reference_lost")}
        </p>
      ) : (
        <Button
          size="sm"
          variant="destructive"
          onClick={() => retryMut.mutate(mediaId)}
          disabled={retryMut.isPending || retryable === null}
        >
          <RefreshCw className={cn("size-4 mr-1.5", retryMut.isPending && "animate-spin")} />
          {t("library.retry_upload")}
        </Button>
      )}
    </div>
  );
}
